/** @jsxImportSource @emotion/react */
import React, { useState } from "react";
import {
    CSSectionCard, CSSectionHeader, CSSectionBody,
    CSTimelineContainer, CSTimelineItem, CSTimelineCard,
    CSTimelineHeader, CSTimelineDate, CSTimelineResult,
    CSTimelineBody, CSTimelineField, CSTimelineFieldLabel, CSTimelineFieldValue,
    CSTimelineActions, CSTimelineBtn,
    CSEmptyState, CSEmptyIcon, CSEmptyTitle, CSEmptyDesc,
    CSStatusBadge, CSTagPill,
} from "./CervicalScreeningStyles";

import {
    SCREENING_METHODS,
    SCREENING_RESULTS,
    TREATMENT_OPTIONS,
    HPV_VACCINATION_STATUSES,
    HIV_STATUS_OPTIONS,
    isScreeningOverdue,
    isScreeningDueSoon,
} from "./hooks/useCervicalScreening";

/* ═══════════════════════════════════════════════════════════════
   CervicalScreeningTimeline — Screening History Timeline (Task 19)
   Shows past cervical cancer screenings chronologically with
   expandable details and edit capability.
   ═══════════════════════════════════════════════════════════════ */

const getMethodLabel = function (value) {
    const found = SCREENING_METHODS.find(function (m) { return m.value === value; });
    return found ? found.label : value;
};

const getResultLabel = function (value) {
    const found = SCREENING_RESULTS.find(function (r) { return r.value === value; });
    return found ? found.label : value;
};

const getResultColor = function (value) {
    const found = SCREENING_RESULTS.find(function (r) { return r.value === value; });
    return found ? found.color : "#37474f";
};

const getTreatmentLabel = function (value) {
    const found = TREATMENT_OPTIONS.find(function (t) { return t.value === value; });
    return found ? found.label : value;
};

const getHPVLabel = function (value) {
    const found = HPV_VACCINATION_STATUSES.find(function (h) { return h.value === value; });
    return found ? found.label : value;
};

const getHIVLabel = function (value) {
    const found = HIV_STATUS_OPTIONS.find(function (h) { return h.value === value; });
    return found ? found.label : value;
};

const formatDate = function (dateStr) {
    if (!dateStr) return "—";
    try {
        const d = new Date(dateStr);
        const options = { year: "numeric", month: "short", day: "numeric" };
        return d.toLocaleDateString("en-GB", options);
    } catch (e) {
        return dateStr;
    }
};

const formatDateTime = function (dateStr) {
    if (!dateStr) return "—";
    try {
        const d = new Date(dateStr);
        const options = { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" };
        return d.toLocaleDateString("en-GB", options);
    } catch (e) {
        return dateStr;
    }
};

export const CervicalScreeningTimeline = function (props) {
    const cs = props.cs;

    /* ─── Track which cards are expanded ─── */
    const expandState = useState({});
    const expanded = expandState[0];
    const setExpanded = expandState[1];

    const toggleExpand = function (id) {
        setExpanded(function (prev) {
            const next = Object.assign({}, prev);
            next[id] = !next[id];
            return next;
        });
    };

    /* ─── Sort records by date descending ─── */
    const sortedRecords = cs.screenings.slice().sort(function (a, b) {
        return new Date(b.screening_date).getTime() - new Date(a.screening_date).getTime();
    });

    /* ─── Empty State ─── */
    if (sortedRecords.length === 0) {
        return (
            <CSSectionCard>
                <CSSectionHeader color="#37474f">
                    Screening History
                </CSSectionHeader>
                <CSSectionBody>
                    <CSEmptyState>
                        <CSEmptyTitle>No Screening Records</CSEmptyTitle>
                        <CSEmptyDesc>
                            No cervical cancer screening records found for this patient.
                            Switch to the Screening tab to record a new screening.
                        </CSEmptyDesc>
                    </CSEmptyState>
                </CSSectionBody>
            </CSSectionCard>
        );
    }

    return (
        <div>
            {/* ─── Timeline ─── */}
            <CSTimelineContainer>
                {sortedRecords.map(function (record) {
                    const isOpen = expanded[record.id];
                    return (
                        <CSTimelineItem key={record.id} result={record.result}>
                            <CSTimelineCard>
                                <CSTimelineHeader onClick={function () { toggleExpand(record.id); }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                                        <CSTimelineDate>
                                            {formatDate(record.screening_date)}
                                        </CSTimelineDate>
                                        <CSStatusBadge color={getResultColor(record.result)}>
                                            {getResultLabel(record.result)}
                                        </CSStatusBadge>
                                        <CSTagPill color="#6a1b9a">
                                            {getMethodLabel(record.screening_method)}
                                        </CSTagPill>
                                        {record.treatment_given && record.treatment_given !== "none" && (
                                            <CSTagPill color="#2e7d32">
                                                {getTreatmentLabel(record.treatment_given)}
                                            </CSTagPill>
                                        )}
                                    </div>
                                    <span style={{ fontSize: "0.85rem", color: "#999", transition: "transform 0.2s", transform: isOpen ? "rotate(180deg)" : "rotate(0)" }}>
                                        ▼
                                    </span>
                                </CSTimelineHeader>

                                <CSTimelineBody open={isOpen}>
                                    {/* ─── Core Details ─── */}
                                    <div style={{ borderLeft: "3px solid #ad145730", paddingLeft: "12px", marginBottom: "12px" }}>
                                        <CSTimelineField>
                                            <CSTimelineFieldLabel>Screening Method</CSTimelineFieldLabel>
                                            <CSTimelineFieldValue>{getMethodLabel(record.screening_method)}</CSTimelineFieldValue>
                                        </CSTimelineField>
                                        <CSTimelineField>
                                            <CSTimelineFieldLabel>Result</CSTimelineFieldLabel>
                                            <CSTimelineFieldValue style={{ color: getResultColor(record.result), fontWeight: 600 }}>
                                                {getResultLabel(record.result)}
                                            </CSTimelineFieldValue>
                                        </CSTimelineField>
                                        {record.hiv_status_at_screening && (
                                            <CSTimelineField>
                                                <CSTimelineFieldLabel>HIV Status</CSTimelineFieldLabel>
                                                <CSTimelineFieldValue>{getHIVLabel(record.hiv_status_at_screening)}</CSTimelineFieldValue>
                                            </CSTimelineField>
                                        )}
                                    </div>

                                    {/* ─── Treatment Details ─── */}
                                    {record.treatment_given && record.treatment_given !== "none" && (
                                        <div style={{ borderLeft: "3px solid #2e7d3230", paddingLeft: "12px", marginBottom: "12px" }}>
                                            <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "#2e7d32", marginBottom: "6px" }}>
                                                Treatment Details
                                            </div>
                                            <CSTimelineField>
                                                <CSTimelineFieldLabel>Treatment Given</CSTimelineFieldLabel>
                                                <CSTimelineFieldValue>{getTreatmentLabel(record.treatment_given)}</CSTimelineFieldValue>
                                            </CSTimelineField>
                                            {record.treatment_date && (
                                                <CSTimelineField>
                                                    <CSTimelineFieldLabel>Treatment Date</CSTimelineFieldLabel>
                                                    <CSTimelineFieldValue>{formatDate(record.treatment_date)}</CSTimelineFieldValue>
                                                </CSTimelineField>
                                            )}
                                            {record.referral_facility && (
                                                <CSTimelineField>
                                                    <CSTimelineFieldLabel>Referral Facility</CSTimelineFieldLabel>
                                                    <CSTimelineFieldValue>{record.referral_facility}</CSTimelineFieldValue>
                                                </CSTimelineField>
                                            )}
                                        </div>
                                    )}

                                    {/* ─── HPV & Follow-up ─── */}
                                    <div style={{ borderLeft: "3px solid #6a1b9a30", paddingLeft: "12px", marginBottom: "12px" }}>
                                        {record.hpv_vaccination_status && (
                                            <CSTimelineField>
                                                <CSTimelineFieldLabel>HPV Vaccination</CSTimelineFieldLabel>
                                                <CSTimelineFieldValue>
                                                    {getHPVLabel(record.hpv_vaccination_status)}
                                                    {record.hpv_vaccination_doses && " — " + record.hpv_vaccination_doses + " dose(s)"}
                                                </CSTimelineFieldValue>
                                            </CSTimelineField>
                                        )}
                                        {record.next_screening_date && (
                                            <CSTimelineField>
                                                <CSTimelineFieldLabel>Next Screening Due</CSTimelineFieldLabel>
                                                <CSTimelineFieldValue style={{
                                                    color: isScreeningOverdue(record.next_screening_date) ? "#c62828"
                                                        : isScreeningDueSoon(record.next_screening_date) ? "#f57f17"
                                                        : "#333",
                                                    fontWeight: isScreeningOverdue(record.next_screening_date) ? 700 : 400,
                                                }}>
                                                    {formatDate(record.next_screening_date)}
                                                    {isScreeningOverdue(record.next_screening_date) && " — OVERDUE"}
                                                </CSTimelineFieldValue>
                                            </CSTimelineField>
                                        )}
                                    </div>

                                    {/* ─── Notes ─── */}
                                    {record.notes && (
                                        <div style={{ borderLeft: "3px solid #37474f30", paddingLeft: "12px", marginBottom: "12px" }}>
                                            <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "#37474f", marginBottom: "4px" }}>
                                                Notes
                                            </div>
                                            <div style={{ fontSize: "0.82rem", color: "#555", lineHeight: 1.5 }}>
                                                {record.notes}
                                            </div>
                                        </div>
                                    )}

                                    {/* ─── Meta ─── */}
                                    <div style={{ fontSize: "0.72rem", color: "#999", marginTop: "8px" }}>
                                        Screened by: {record.screened_by || "—"} &nbsp;•&nbsp; Recorded: {formatDateTime(record.created_at)}
                                    </div>
                                </CSTimelineBody>

                                {/* ─── Actions ─── */}
                                {isOpen && (
                                    <CSTimelineActions>
                                        <CSTimelineBtn
                                            color="#ad1457"
                                            onClick={function () { cs.startEdit(record); }}
                                        >
                                            Edit
                                        </CSTimelineBtn>
                                    </CSTimelineActions>
                                )}
                            </CSTimelineCard>
                        </CSTimelineItem>
                    );
                })}
            </CSTimelineContainer>
        </div>
    );
};
